import Cookies from "js-cookie";
import type { BaseSettings } from "@/components/Settings/types/settings";

export type ConsentCategory = "preferences" | "statistics" | "marketing";

const DEFAULT_GROUPS: Record<ConsentCategory, string> = {
  preferences: "C0003",
  statistics: "C0002",
  marketing: "C0004",
};

let currentSettings: BaseSettings | undefined;

const getActiveOneTrustGroups = (): Set<string> => {
  const groups = window.OptanonActiveGroups ?? "";
  return new Set(groups.split(",").filter(Boolean));
};

export const configureConsent = (settings?: BaseSettings) => {
  currentSettings = settings;
};

const isValidOneTrustUrl = (scriptSrc?: string) => {
  if (!scriptSrc) return true;
  try {
    return new URL(scriptSrc).protocol === "https:";
  } catch {
    return false;
  }
};

export const isOneTrustEnabled = (settings = currentSettings) => {
  const oneTrust = settings?.consentManager?.oneTrust;
  return (
    settings?.consentManager?.provider === "onetrust" &&
    !!oneTrust?.domainScript &&
    /^[a-zA-Z0-9-]+$/.test(oneTrust.domainScript) &&
    isValidOneTrustUrl(oneTrust.scriptSrc)
  );
};

// True once the OneTrust SDK has loaded and can show its preference center.
export const isOneTrustLoaded = () => !!window.OneTrust;

export const hasConsent = (
  category: ConsentCategory,
  settings = currentSettings,
) => {
  if (isOneTrustEnabled(settings)) {
    const configuredGroups = settings?.consentManager?.oneTrust?.groups;
    const group = configuredGroups?.[category] ?? DEFAULT_GROUPS[category];
    return getActiveOneTrustGroups().has(group);
  }

  return Cookies.get(`rcl_${category}_consent`) === "true";
};

// Dispatched whenever the user's consent may have changed (OneTrust or legacy dialog).
export const CONSENT_CHANGED_EVENT = "ov:consent-changed";
// Asks the legacy cookie dialog to reopen its preferences.
export const OPEN_LEGACY_COOKIE_SETTINGS_EVENT = "ov:open-legacy-cookie-settings";

export const notifyConsentChanged = () => {
  window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
};

// Lets the user review or withdraw their consent with whichever manager is active.
export const openCookieSettings = () => {
  if (isOneTrustEnabled()) {
    window.OneTrust?.ToggleInfoDisplay();
  } else {
    window.dispatchEvent(new Event(OPEN_LEGACY_COOKIE_SETTINGS_EVENT));
  }
};

declare global {
  interface Window {
    OneTrust?: {
      ToggleInfoDisplay: () => void;
    };
    OptanonActiveGroups?: string;
    OptanonWrapper?: () => void;
  }
}
